import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../api/client';

const SETTING_TABS = [
  { id: 'system', label: 'Sistema', icon: '⚙️', roles: ['admin'] },
  { id: 'stream', label: 'Streaming', icon: '📡', roles: ['admin', 'operator'] },
  { id: 'automation', label: 'Automatización', icon: '🤖', roles: ['admin', 'operator'] },
  { id: 'library', label: 'Biblioteca', icon: '🎵', roles: ['admin', 'operator', 'editor'] },
  { id: 'ads', label: 'Publicidad', icon: '📢', roles: ['admin', 'operator', 'advertising'] },
  { id: 'alerts', label: 'Alertas', icon: '🔔', roles: ['admin', 'operator', 'editor', 'dj', 'advertising'] },
  { id: 'history', label: 'Historial', icon: '📚', roles: ['admin', 'operator', 'editor', 'dj', 'advertising'] },
  { id: 'security', label: 'Seguridad', icon: '🔒', roles: ['admin'] },
  { id: 'logs', label: 'Registros', icon: '📋', roles: ['admin', 'operator'] },
];

function SystemSettingsForm() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/api/settings/system');
      setSettings(data.settings || data);
    } catch (err) {
      console.error('[SettingsPanel] Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) =>
      prev.map((s) => (s.key === key ? { ...s, value } : s))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      const values = {};
      settings.forEach((s) => {
        values[s.key] = s.value;
      });
      await api.put('/api/settings/system', { settings: values });
      setSaveMessage('Ajustes guardados correctamente.');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('[SettingsPanel] Save failed:', err);
      setSaveMessage('Error al guardar los ajustes.');
    } finally {
      setSaving(false);
    }
  };

  const groupedSettings = () => {
    const groups = {};
    settings.forEach((s) => {
      const group = s.group || 'General';
      if (!groups[group]) groups[group] = [];
      groups[group].push(s);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-dark-400">
        <p>Cargando ajustes...</p>
      </div>
    );
  }

  const groups = groupedSettings();

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-dark-100">Ajustes del Sistema</h2>

      {saveMessage && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            saveMessage.includes('success')
              ? 'bg-success-900/30 border-success-700 text-success-400'
              : 'bg-error-900/30 border-error-700 text-error-400'
          }`}
        >
          {saveMessage}
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(groups).map(([groupName, groupSettings]) => (
          <div key={groupName} className="rounded-xl bg-dark-800/50 border border-dark-700 p-4">
            <h3 className="text-sm font-medium text-dark-300 mb-3">{groupName}</h3>
            <div className="space-y-4">
              {groupSettings.map((setting) => (
                <div key={setting.key} className="flex items-center justify-between py-2 border-b border-dark-700 last:border-0">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-dark-300">{setting.label}</label>
                    {setting.description && (
                      <p className="text-xs text-dark-500 mt-0.5">{setting.description}</p>
                    )}
                  </div>
                  <div className="w-64 ml-4">
                    {setting.type === 'checkbox' ? (
                      <div className="flex justify-end">
                        <input
                          type="checkbox"
                          checked={setting.value === true || setting.value === 'true'}
                          onChange={(e) => handleChange(setting.key, e.target.checked)}
                          className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
                        />
                      </div>
                    ) : setting.type === 'select' ? (
                      <select
                        value={setting.value || ''}
                        onChange={(e) => handleChange(setting.key, e.target.value)}
                        className="input text-sm"
                      >
                        {(setting.options || []).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : setting.type === 'number' ? (
                      <input
                        type="number"
                        value={setting.value || ''}
                        onChange={(e) => handleChange(setting.key, e.target.value ? parseInt(e.target.value, 10) : null)}
                        className="input text-sm"
                      />
                    ) : (
                      <input
                        type="text"
                        value={setting.value || ''}
                        onChange={(e) => handleChange(setting.key, e.target.value)}
                        className="input text-sm"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-4">
        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? 'Guardando...' : 'Guardar Ajustes'}
        </button>
      </div>
    </div>
  );
}

export default function SettingsPanel() {
  const location = useLocation();
  const currentTab = location.pathname === '/settings/system' ? 'system' : 'system';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Ajustes</h1>
      </div>

      <div className="rounded-xl bg-dark-850 border border-dark-700">
        <div className="border-b border-dark-700">
          <nav className="flex flex-wrap -mx-4">
            {SETTING_TABS.map((tab) => (
              <Link
                key={tab.id}
                to={tab.id === 'system' ? '/settings' : `/settings/${tab.id}`}
                className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${
                  currentTab === tab.id
                    ? 'border-primary-500 text-primary-400'
                    : 'border-transparent text-dark-400 hover:border-dark-600 hover:text-dark-200'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="p-6">
          <SystemSettingsForm />
        </div>
      </div>
    </div>
  );
}
